import type {FtpSubfolderRole} from '@common/groupFtp/types';

// String unions mirror the Prisma enums in groupPipeline.prisma.

// Phase of a group whose pipeline is finished, or that has none.
export const ACTIVE_PHASE = 'Active';

export type PipelineActor = 'CBS' | 'EMPLOYER' | 'BROKER' | 'EXTERNAL';

export type PipelineDocumentKind = 'INTAKE' | 'GO_LIVE_PACKAGE';

// Each rule type is implemented in code; definitions only choose from this list.
export type PipelineTaskRule = {type: 'completeWhenDocsReceived'} | {type: 'onRejectSendBack'};

export type PipelineTaskRules = PipelineTaskRule[];

export interface PipelineTaskDefinitionSeed {
  key: string;
  label: string;
  section: string;
  actor: PipelineActor;
  dependsOn: string[];
  isRequired: boolean;
  isRepeatable: boolean;
  needsApproval: boolean;
  targetDays: number | null;
  rules: PipelineTaskRules;
}

export interface PipelineDocumentDefinitionSeed {
  key: string;
  label: string;
  kind: PipelineDocumentKind;
  actor: PipelineActor;
  taskKey: string | null;
  ftpDestination?: FtpSubfolderRole;
}

export interface TenantPipelineSeed {
  // Client.vbaShortClientName of the tenant this list belongs to.
  clientShortName: string;
  tasks: PipelineTaskDefinitionSeed[];
  documents: PipelineDocumentDefinitionSeed[];
}

export type UserSavedViewConfig = Record<string, unknown>;

export type PipelineEventData = Record<string, string | number | boolean | null>;

// Activity log filters, named after the detail page area each event comes from. A new tab that
// logs events adds its category here.
export const ACTIVITY_CATEGORIES = ['tasks', 'documents', 'status'] as const;
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategory, string> = {
  tasks: 'Tasks',
  documents: 'Documents',
  status: 'Status',
};

// Every pipeline event type, with the activity filter it shows under.
export const GROUP_PIPELINE_EVENTS = {
  PIPELINE_STARTED: 'status',
  PIPELINE_LIVE: 'status',
  TASK_COMPLETED: 'tasks',
  TASK_SKIPPED: 'tasks',
  TASK_REOPENED: 'tasks',
  TASK_SENT_BACK: 'tasks',
  TASK_COMPLETED_AT_CHANGED: 'tasks',
  TASK_RENAMED: 'tasks',
  TASK_DUE_DATE_CHANGED: 'tasks',
  TASK_ADDED: 'tasks',
  PIPELINE_ON_HOLD: 'status',
  PIPELINE_RESUMED: 'status',
  PIPELINE_NOT_MOVING_FORWARD: 'status',
  PIPELINE_REINSTATED: 'status',
  PIPELINE_MOVED_TO_STEP: 'tasks',
  FTP_FOLDER_LINKED: 'documents',
  FTP_SUBFOLDERS_UPDATED: 'documents',
  DOCUMENT_FILE_LINKED: 'documents',
  DOCUMENT_FILE_UNLINKED: 'documents',
  DOCUMENT_FILE_UPLOADED: 'documents',
  FTP_FILE_ACCESSED: 'documents',
} as const satisfies Record<string, ActivityCategory>;

export type GroupPipelineEventType = keyof typeof GROUP_PIPELINE_EVENTS;

export const PIPELINE_STATUS_ACTIONS = ['hold', 'resume', 'notMovingForward', 'reinstate'] as const;
export type PipelineStatusAction = (typeof PIPELINE_STATUS_ACTIONS)[number];

export const activityEventTypes = (category: ActivityCategory) =>
  Object.entries(GROUP_PIPELINE_EVENTS).flatMap(([type, c]) => (c === category ? [type] : []));

const isGroupPipelineEventType = (type: string): type is GroupPipelineEventType =>
  Object.hasOwn(GROUP_PIPELINE_EVENTS, type);

// Null for a type no longer in the list; those still show under All.
export const activityCategoryOf = (type: string): ActivityCategory | null =>
  isGroupPipelineEventType(type) ? GROUP_PIPELINE_EVENTS[type] : null;

// Whether VBA has the group yet. Worked out from VBA on read, never stored.
export const VBA_STATUSES = ['NOT_IN_VBA', 'IN_VBA'] as const;
export type VbaStatus = (typeof VBA_STATUSES)[number];
